import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

import { ViewContent } from '@/components/view-layout';
import { useIngredient } from '@/features/ingredients/api/ingredients-queries';
import { IngredientCostForm } from '@/features/ingredients/components/ingredient-cost-form';
import { IngredientDetailsForm } from '@/features/ingredients/components/ingredient-details-form';
import { IngredientEditHeader } from '@/features/ingredients/components/ingredient-edit-header';
import { IngredientEditSectionTitle } from '@/features/ingredients/components/ingredient-edit-section-title';
import { IngredientEditTabs } from '@/features/ingredients/components/ingredient-edit-tabs';
import { IngredientRecipeTable } from '@/features/ingredients/components/ingredient-recipe-table';
import { useOrganisation } from '@/features/organisations/api/organisation-queries';
import { useSuppliers } from '@/features/suppliers/api/supplier-queries';

function IngredientEditContent() {
  const { ingredientId, tenantId } = Route.useParams();
  const { auth } = Route.useRouteContext();

  const { data: ingredient } = useIngredient(ingredientId, tenantId);
  const { data: suppliers } = useSuppliers(tenantId);
  const { data: organisation } = useOrganisation(tenantId);

  if (!ingredient || !organisation) {
    // TODO: Handle loading state or error state
    return <div>Error: Ingredient not found</div>;
  }

  const isEditable =
    auth.hasRole(['admin', 'editor'], tenantId) &&
    ingredient.organisation_id === tenantId;

  return (
    <>
      <IngredientEditHeader ingredient={ingredient} isEditable={isEditable} />
      <ViewContent>
        <div className="flex h-full pt-6">
          <div className="flex flex-col flex-1 px-6">
            <div>
              <IngredientEditSectionTitle>Details</IngredientEditSectionTitle>
              <IngredientDetailsForm
                ingredient={ingredient}
                suppliers={suppliers}
                isEditable={isEditable}
              />
            </div>
            <div className="mt-6">
              <IngredientEditSectionTitle>Cost</IngredientEditSectionTitle>
              <IngredientCostForm
                ingredient={ingredient}
                organisationId={tenantId}
                isPriceEditable={auth.hasRole(['admin', 'editor'], tenantId)} // Users can edit prices of managed ingredients
                isQuantityEditable={isEditable}
                currencyCode={organisation.currency}
                measurementSystem={organisation.measurement_system}
              />
            </div>
            {/* TODO: Standarize this mt-6 for recipe and ingredient edit pages*/}
            <div className="mt-6">
              <IngredientRecipeTable
                ingredientId={ingredientId}
                organisationId={tenantId}
                isEditable={isEditable}
              />
            </div>
          </div>
          <div className="flex flex-col flex-1">
            <IngredientEditTabs
              ingredient={ingredient}
              isEditable={isEditable}
            />
          </div>
        </div>
      </ViewContent>
    </>
  );
}

export function IngredientEditPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <IngredientEditContent />
    </Suspense>
  );
}

export const Route = createFileRoute(
  '/_authenticated/$tenantId/ingredients/$ingredientId',
)({
  component: IngredientEditPage,
});
